import React from 'react';
import { useLocale } from '../i18n/LocaleProvider';
import { Link } from 'react-router-dom';
import {
  ShieldCheck, AlertTriangle, EyeOff, TrendingDown,
  Shuffle, BarChart2, XCircle, Smartphone, ArrowRight, Sparkles,
  Palette, User, Clock, Users, MessageCircle, Globe, Heart, Gift
} from 'lucide-react';
import useInView from '../hooks/useInView';
import AppShowcaseSection from '../sections/AppShowcaseSection';
import AcademyTeaserSection from '../sections/AcademyTeaserSection';
import VentureStudioTeaserSection from '../sections/VentureStudioTeaserSection';
import DesignShowcaseSection from '../sections/DesignShowcaseSection';
import HowItWorksSection from '../sections/HowItWorksSection';
import WhySection from '../sections/WhySection';
import NewsletterSection from '../sections/NewsletterSection';
import CoreFeaturesSection from '../sections/CoreFeaturesSection';
import { RENDERS, RENDER_LIST } from '../data/renders';

const PLAY_URL = "https://play.google.com/store/apps/details?id=tech.romindkids.kids";
const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/69ab8de2-9056-4863-b71b-4d5b72155bf7/images/810acbfb8d424212c35b7fe1d504594f90cb4b1ac36881dc1cb32a1fdc823cc2.png";
const PLANET = "/images/home/solution-ecosystem.png";
const SHIELD = "/images/home/safety-research-hero.png";
const RONI = "/images/home/roni-feature.png";

const AppMockup = () => (
  <div className="relative mx-auto w-[280px] sm:w-[320px] lg:w-[360px]">
    {/* Cosmic glow halo */}
    <div className="absolute -inset-8 bg-purple-500/30 rounded-[64px] blur-[80px] animate-glow-pulse pointer-events-none" />
    <div className="absolute -inset-6 bg-cyan-500/15 rounded-[64px] blur-[60px] pointer-events-none" />
    {/* The render already includes a phone frame — render it directly */}
    <img
      src={RENDERS.profile}
      alt="ROMIND KIDS — Profile screen"
      className="relative w-full h-auto select-none animate-float drop-shadow-[0_20px_60px_rgba(157,76,221,0.35)]"
      draggable="false"
    />
  </div>
);

const problems = [
  { icon: TrendingDown, title: "Endless Scrolling", desc: "Platforms optimized for engagement trap children in infinite loops with no purposeful direction." },
  { icon: AlertTriangle, title: "Unsafe Interactions", desc: "No age-specific filters. Children encounter content and accounts not designed for their emotional stage." },
  { icon: XCircle, title: "No Child-Specific Design", desc: "Adult social networks resized for younger users — not platforms built around children's actual needs." },
  { icon: BarChart2, title: "Pressure & Comparison", desc: "Like counts, follower metrics, and popularity culture create measurable stress in the 9–14 age group." },
  { icon: EyeOff, title: "Invisible to Parents", desc: "Parents have little clarity on what their child experiences. Trust is built on hope, not structure." },
  { icon: Shuffle, title: "Chaos Over Creativity", desc: "Chaotic feeds and random trending content crowd out the creative expression children actually seek." },
];

const safetyPrinciples = [
  "Designed for ages 9–14 with age-aware experience logic",
  "RONI AI-assisted safety guidance built in",
  "Reporting and blocking flows on all content",
  "Safer messaging with friend-only communication",
  "No precise location exposure by design",
  "Privacy-conscious public identity structure",
  "No negative reaction culture or dislike mechanics",
  "No adult dating positioning or gambling mechanics",
  "No real-money random reward mechanics",
  "Clear separation of creativity, communication, and safety controls",
  "Parent-aware onboarding approach",
  "Prepared for policy-conscious platform review",
];

const roadmapPhases = [
  { phase: "01", title: "Core Platform", items: ["Multi-level Feed", "Create Studio", "Child Profiles", "Friends System"], status: "Completed" },
  { phase: "02", title: "Safety Layer", items: ["RONI AI Companion", "Reporting Flows", "Blocking System", "Safe Messaging"], status: "Completed" },
  { phase: "03", title: "Parent Controls", items: ["Parent Portal", "Activity Insights", "Safety Reports", "Age Controls"], status: "Completed" },
  { phase: "04", title: "Community & Times", items: ["Clubs", "Challenges", "Times System", "Gifting Mechanics"], status: "Completed" },
  { phase: "05", title: "Pre-Launch Refinement", items: ["Full-System Debugging", "Product Design Polish", "Closed Testing", "Safety Hardening"], status: "Completed" },
  { phase: "06", title: "Android Launch & Growth", items: ["Google Play Released", "App Store (Planned)", "Mobile-First UX", "Launch Campaign"], status: "Current Phase" },
];

const SectionLabel = ({ children }) => (
  <p className="text-xs font-semibold tracking-[0.2em] uppercase text-purple-400 mb-3 font-outfit">{children}</p>
);

const FadeIn = ({ children, delay = 0, className = '' }) => {
  const [ref, inView] = useInView(0.1);
  return (
    <div ref={ref} className={`transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'} ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
};

const DownloadButton = () => {
  const { t } = useLocale();
  return (
    <a href={PLAY_URL} target="_blank" rel="noopener noreferrer" data-testid="google-play-download"
      className="inline-flex min-h-12 items-center justify-center gap-2 rounded-full bg-gradient-to-r from-purple-600 via-fuchsia-600 to-indigo-600 px-7 py-3 text-center text-sm font-extrabold text-white shadow-[0_10px_35px_rgba(168,85,247,0.4)] transition hover:-translate-y-0.5 hover:brightness-110 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-cyan-300"
    ><Smartphone size={17} aria-hidden="true" />{t('download.cta')}</a>
  );
};

const DownloadQR = ({ compact = false }) => {
  const { t } = useLocale();
  return (
    <div className="inline-flex items-center gap-4">
      <img
        src="/images/google-play/romindkids-google-play-qr.png"
        alt={t('download.qrAlt')}
        className={`shrink-0 rounded-2xl border border-purple-300/40 bg-white p-2 shadow-[0_0_30px_rgba(168,85,247,0.2)] ${compact ? 'h-28 w-28' : 'h-48 w-48'}`}
        loading="lazy"
      />
      <p className="max-w-[180px] text-left text-sm font-medium leading-6 text-white/70">{t('download.scan')}</p>
    </div>
  );
};

const HeroSection = () => (
  <section
    data-testid="hero-section"
    className="relative min-h-screen flex items-center pt-16 overflow-hidden"
    style={{ backgroundImage: `url(${HERO_BG})`, backgroundSize: 'cover', backgroundPosition: 'center top' }}
  >
    <div className="absolute inset-0 bg-gradient-to-b from-[#05050A]/70 via-[#05050A]/50 to-[#05050A]" />
    <div className="absolute top-1/4 left-1/4 w-80 h-80 bg-purple-600/20 rounded-full blur-[120px] pointer-events-none animate-nebula" />
    <div className="absolute bottom-1/3 right-1/4 w-64 h-64 bg-cyan-500/15 rounded-full blur-[100px] pointer-events-none" />
    <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 w-full">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <div>
          <SectionLabel>Introducing ROMIND KIDS</SectionLabel>
          <h1 className="font-outfit font-black text-5xl sm:text-6xl lg:text-7xl text-white leading-[1.05] tracking-tight mb-6">
            A creative<br />
            <span className="gradient-text">universe</span> for kids.<br />
            <span className="text-white/85 text-3xl sm:text-4xl lg:text-5xl font-bold">A safety layer for parents.</span>
          </h1>
          <p className="text-white/60 text-base sm:text-lg leading-relaxed mb-8 max-w-xl">
            ROMIND KIDS is a premium social and creative platform — designed inside for children aged 9–14, and designed around for the parents who trust it. The app experience is built for how kids actually create, communicate, and connect. The safety, privacy and trust architecture is built for adults who need clarity, not surveillance.
          </p>
          <div className="flex flex-wrap gap-3">
            <DownloadButton />
            <Link to="/features" data-testid="hero-cta-explore" className="px-6 py-3 rounded-full font-semibold text-sm bg-white/5 border border-white/15 text-white hover:bg-white/10 hover:border-purple-500/40 transition-all duration-200">
              Explore the Platform
            </Link>
            <Link to="/parents" data-testid="hero-cta-parents" className="px-6 py-3 rounded-full font-semibold text-sm bg-white/5 border border-white/15 text-white hover:bg-white/10 hover:border-cyan-500/40 hover:text-cyan-400 transition-all duration-200">
              Parent Safety Overview
            </Link>
          </div>
          <div className="mt-6 hidden sm:block"><DownloadQR compact /></div>
        </div>
        <div className="flex justify-center lg:justify-end">
          <AppMockup />
        </div>
      </div>
    </div>
  </section>
);

const GiftCampaignSection = () => (
  <section
    data-testid="gift-campaign-section"
    className="relative overflow-hidden border-y border-fuchsia-400/20 bg-gradient-to-r from-[#100822] via-[#090815] to-[#061722] py-14"
  >
    <div className="pointer-events-none absolute -left-20 top-1/2 h-72 w-72 -translate-y-1/2 rounded-full bg-purple-600/25 blur-[100px]" />
    <div className="pointer-events-none absolute right-[12%] top-1/2 h-64 w-64 -translate-y-1/2 rounded-full bg-cyan-500/20 blur-[100px]" />
    <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-fuchsia-300/70 to-transparent" />
    <div className="relative mx-auto grid max-w-7xl items-center gap-8 px-4 sm:px-6 lg:grid-cols-[1fr_auto] lg:px-8">
      <div className="flex items-start gap-4 sm:gap-6">
        <div className="relative flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-2xl border border-fuchsia-300/35 bg-gradient-to-br from-purple-500/25 to-fuchsia-500/15 text-fuchsia-100 shadow-[0_0_45px_rgba(217,70,239,0.2)]">
          <div className="absolute inset-0 animate-pulse rounded-2xl border border-fuchsia-300/15" />
          <Gift size={25} />
        </div>
        <div>
          <p className="mb-2 text-xs font-extrabold uppercase tracking-[0.2em] text-fuchsia-300">
            A launch gift for the first 100 families
          </p>
          <h2 className="max-w-3xl font-outfit text-3xl font-black leading-tight text-white sm:text-4xl">
            Give your child{" "}
            <span className="gradient-text">
              90 days in a universe made for them.
            </span>
          </h2>
          <p className="mt-3 max-w-3xl text-sm leading-7 text-white/60 sm:text-base">
            Academy, interest clubs, creative tools, Challenges, Rewards, safer
            communication and Parent Controls — choose any of five plans and
            experience ROMIND KIDS without payment or automatic renewal.
          </p>
          <div className="mt-5 flex flex-wrap gap-2">
            {["Ages 9–14", "90 full days", "Only 100 family gifts"].map(
              (item) => (
                <span
                  key={item}
                  className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.05] px-3 py-1.5 text-[11px] font-semibold text-white/70"
                >
                  <Sparkles size={11} className="text-cyan-300" /> {item}
                </span>
              ),
            )}
          </div>
        </div>
      </div>
      <div className="lg:text-right">
        <Link
          to="/gift"
          data-testid="home-gift-cta"
          className="group inline-flex min-h-14 w-full items-center justify-center gap-2 rounded-full border border-fuchsia-200/55 bg-gradient-to-r from-purple-600 via-fuchsia-600 to-pink-500 px-8 text-sm font-extrabold text-white shadow-[0_14px_45px_rgba(168,85,247,0.35)] transition hover:-translate-y-0.5 hover:brightness-110 sm:w-auto"
        >
          Claim 90 days free{" "}
          <ArrowRight
            size={18}
            className="transition-transform group-hover:translate-x-1"
          />
        </Link>
        <p className="mt-3 text-center text-[11px] text-white/35">
          Live availability · a place is held only after parent sign-in
        </p>
      </div>
    </div>
  </section>
);

const ProblemSection = () => {
  const [ref, inView] = useInView(0.05);
  return (
    <section data-testid="problem-section" className="relative py-24 bg-[#080714] overflow-hidden">
      <div className="absolute top-0 right-0 w-96 h-96 bg-pink-600/10 rounded-full blur-[120px] pointer-events-none" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <FadeIn><SectionLabel>The Problem</SectionLabel></FadeIn>
        <FadeIn delay={100}>
          <h2 className="font-outfit font-bold text-3xl sm:text-4xl text-white mb-4 tracking-tight max-w-2xl">
            The digital world wasn't built for children.
          </h2>
        </FadeIn>
        <FadeIn delay={200}>
          <p className="text-white/50 text-base mb-12 max-w-xl">
            Traditional social platforms are designed around attention capture — not child wellbeing, emotional safety, or family trust.
          </p>
        </FadeIn>
        <div ref={ref} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {problems.map((p, i) => (
            <div
              key={p.title}
              data-testid={`problem-card-${i}`}
              className={`glass-card p-6 transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: `${i * 80}ms` }}
            >
              <div className="w-9 h-9 rounded-xl bg-red-500/10 border border-red-500/20 flex items-center justify-center mb-4">
                <p.icon size={17} className="text-red-400" />
              </div>
              <h3 className="font-outfit font-semibold text-white text-base mb-2">{p.title}</h3>
              <p className="text-white/45 text-sm leading-relaxed">{p.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const SolutionSection = () => (
  <section data-testid="solution-section" className="relative py-24 overflow-hidden">
    <div className="absolute top-1/2 left-0 w-80 h-80 bg-cyan-500/10 rounded-full blur-[100px] pointer-events-none" />
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <FadeIn>
          <SectionLabel>The Solution</SectionLabel>
          <h2 className="font-outfit font-bold text-3xl sm:text-4xl text-white mb-6 tracking-tight">
            Not another social app —<br />
            <span className="gradient-text-cool">a creative ecosystem</span> for kids, with a safety layer for parents.
          </h2>
          <p className="text-white/55 text-base leading-relaxed mb-6">
            The inside of ROMIND KIDS — feed, clubs, challenges, Create Studio, identity — is shaped around how children aged 9–14 actually create and connect. The privacy, safety, and trust architecture is shaped for the parents who decide what apps enter their home.
          </p>
          <div className="space-y-3">
            {["Interest-based structure, not algorithmic chaos","Child-safe communication with AI guidance","Creative tools that encourage purposeful participation","Parent-aware design without surveillance culture"].map((item, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-5 h-5 rounded-full bg-purple-500/20 border border-purple-500/40 flex items-center justify-center flex-shrink-0">
                  <div className="w-2 h-2 rounded-full bg-purple-400" />
                </div>
                <span className="text-white/65 text-sm">{item}</span>
              </div>
            ))}
          </div>
        </FadeIn>
        <FadeIn delay={200} className="flex justify-center">
          <div className="relative">
            <div className="absolute inset-0 bg-purple-500/20 rounded-full blur-[80px]" />
            <img src={PLANET} alt="Cosmic environment" className="relative w-full max-w-sm rounded-3xl opacity-90" />
          </div>
        </FadeIn>
      </div>
    </div>
  </section>
);

const FeaturesSection = () => (
  <div className="bg-[#080714]">
    <CoreFeaturesSection
      heading="Core Features"
      subtitle="Explore the systems that make ROMIND KIDS a safer creative universe for children aged 9–14. Hover any card for a preview, tap to open the full deep dive."
    />
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6 pb-8 text-center">
      <Link to="/features" data-testid="features-learn-more" className="inline-flex items-center gap-2 text-sm text-purple-400 hover:text-purple-300 transition-colors font-medium">
        See the full Feature Explorer <ArrowRight size={14} />
      </Link>
    </div>
  </div>
);

const SafetySection = () => (
  <section data-testid="safety-section" className="relative py-24 overflow-hidden">
    <div className="absolute top-1/2 right-0 w-80 h-80 bg-emerald-500/10 rounded-full blur-[100px] pointer-events-none" />
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <FadeIn className="order-2 lg:order-1">
          <SectionLabel>Safety & Trust</SectionLabel>
          <h2 className="font-outfit font-bold text-3xl sm:text-4xl text-white mb-4 tracking-tight">
            Safety isn't a feature.<br />
            <span className="gradient-text-cool">It's the foundation.</span>
          </h2>
          <p className="text-white/55 text-base leading-relaxed mb-8">
            ROMIND KIDS is designed with child safety principles at every layer. Not as an add-on — as the core of every product decision.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            {safetyPrinciples.map((p, i) => (
              <div key={i} data-testid={`safety-principle-${i}`} className="flex items-start gap-2.5">
                <div className="w-4 h-4 rounded-full bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                </div>
                <span className="text-white/60 text-xs leading-relaxed">{p}</span>
              </div>
            ))}
          </div>
          <div className="mt-8 p-4 rounded-xl bg-white/3 border border-white/8">
            <p className="text-white/35 text-xs leading-relaxed">
              <strong className="text-white/50">Note:</strong> ROMIND KIDS is designed with child safety principles and parent-aware architecture. Claims of specific legal certification require formal independent review.
            </p>
          </div>
        </FadeIn>
        <FadeIn delay={200} className="order-1 lg:order-2 flex justify-center">
          <div className="relative">
            <div className="absolute inset-0 bg-emerald-500/15 rounded-full blur-[80px]" />
            <img src={SHIELD} alt="Safety shield" className="relative w-full max-w-xs" />
          </div>
        </FadeIn>
      </div>
    </div>
  </section>
);

const ProductPreviewSection = () => (
  <section data-testid="product-preview-section" className="relative py-24 bg-[#080714] overflow-hidden">
    <div className="absolute top-1/2 left-0 w-80 h-80 bg-violet-600/12 rounded-full blur-[140px] pointer-events-none" />
    <div className="absolute bottom-0 right-0 w-72 h-72 bg-cyan-500/10 rounded-full blur-[120px] pointer-events-none" />
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <FadeIn><SectionLabel>Product Preview</SectionLabel></FadeIn>
      <FadeIn delay={100}>
        <h2 className="font-outfit font-bold text-3xl sm:text-4xl text-white mb-4 tracking-tight">Real screens from inside ROMIND KIDS.</h2>
        <p className="text-white/50 text-base mb-12 max-w-xl">Five core screens that define the experience — Profile, Story Portal, RONI, Chat, and Times Cards. Visuals are in active development; final UI may evolve.</p>
      </FadeIn>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 lg:gap-4">
        {RENDER_LIST.map((r, i) => (
          <FadeIn key={r.id} delay={i * 80}>
            <div data-testid={`product-preview-${r.id}`} className="group flex flex-col items-center">
              <div className="relative w-full max-w-[260px] mx-auto">
                <div className={`absolute -inset-3 rounded-[48px] bg-gradient-to-br ${r.glow} blur-[40px] opacity-70 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none`} />
                <img
                  src={r.src}
                  alt={`ROMIND KIDS — ${r.title}`}
                  className="relative w-full h-auto select-none transition-transform duration-500 group-hover:-translate-y-1"
                  draggable="false"
                  loading="lazy"
                />
              </div>
              <p className="font-outfit font-semibold text-white text-sm mt-4">{r.title}</p>
              <p className="text-white/45 text-xs text-center mt-1 max-w-[220px] leading-relaxed">{r.caption}</p>
            </div>
          </FadeIn>
        ))}
      </div>
    </div>
  </section>
);

const ForParentsSection = () => (
  <section data-testid="for-parents-section" className="relative py-24 overflow-hidden">
    <div className="absolute top-1/2 left-0 w-72 h-72 bg-purple-600/12 rounded-full blur-[100px] pointer-events-none" />
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <FadeIn>
          <SectionLabel>For Parents</SectionLabel>
          <h2 className="font-outfit font-bold text-3xl sm:text-4xl text-white mb-4 tracking-tight">
            The app is for your child.<br /><span className="gradient-text-cool">The trust layer is for you.</span>
          </h2>
          <p className="text-white/55 text-base leading-relaxed mb-8">
            We don't ask parents to learn another kids' app. We give parents the clarity, safety architecture, and design transparency that lets them say "yes" to their child without anxiety.
          </p>
          <Link to="/parents" data-testid="parents-learn-more" className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm bg-white/5 border border-white/15 text-white hover:bg-white/10 hover:border-purple-500/40 transition-all duration-200">
            Parent Safety Overview <ArrowRight size={14} />
          </Link>
        </FadeIn>
        <FadeIn delay={200}>
          <div className="space-y-3">
            {[
              { title: "Clearer digital boundaries", desc: "Structured content categories and age-aware design keep the experience focused." },
              { title: "Safer communication environment", desc: "Friend-based messaging with AI-assisted safety signals and moderation flows." },
              { title: "Child-focused design philosophy", desc: "Built around the emotional and creative needs of the 9–14 age group." },
              { title: "Less meaningless scrolling", desc: "Interest-based navigation and clubs encourage purposeful participation." },
              { title: "More creativity", desc: "Tools for expression, challenges, and clubs replace passive consumption." },
            ].map((item, i) => (
              <div key={i} className="glass-card p-4 flex items-start gap-3">
                <div className="w-7 h-7 rounded-lg bg-purple-500/15 border border-purple-500/25 flex items-center justify-center flex-shrink-0">
                  <ShieldCheck size={13} className="text-purple-400" />
                </div>
                <div><p className="font-outfit font-semibold text-white text-sm mb-0.5">{item.title}</p><p className="text-white/45 text-xs">{item.desc}</p></div>
              </div>
            ))}
          </div>
        </FadeIn>
      </div>
    </div>
  </section>
);

const ForKidsSection = () => (
  <section data-testid="for-kids-section" className="relative py-24 bg-[#080714] overflow-hidden">
    <div className="absolute top-1/3 right-0 w-80 h-80 bg-fuchsia-600/12 rounded-full blur-[120px] pointer-events-none" />
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <FadeIn><SectionLabel>For Kids</SectionLabel></FadeIn>
      <FadeIn delay={100}>
        <h2 className="font-outfit font-bold text-3xl sm:text-4xl text-white mb-4 tracking-tight">
          Your creative universe awaits.
        </h2>
        <p className="text-white/50 text-base mb-12 max-w-xl">
          Express yourself, connect with friends, discover interests, and explore a platform designed to inspire — not overwhelm.
        </p>
      </FadeIn>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {[
          { icon: Palette, label: "Create Posts", sub: "Share photos, videos & stories" },
          { icon: User, label: "Customize Profile", sub: "Avatars, auras & frames" },
          { icon: Clock, label: "Collect Times Cards", sub: "Share limited-time gifts" },
          { icon: Users, label: "Join Clubs", sub: "Connect around interests" },
          { icon: Sparkles, label: "Take Challenges", sub: "Creative community goals" },
          { icon: MessageCircle, label: "Chat Safely", sub: "Friends only, RONI-guided" },
          { icon: Globe, label: "Meet Peers Globally", sub: "Cultural exchange via creativity" },
          { icon: Heart, label: "Express Yourself", sub: "Your voice, your way" },
        ].map((item, i) => (
          <FadeIn key={item.label} delay={i * 50}>
            <div data-testid={`kids-card-${i}`} className="glass-card p-5 text-center hover:border-purple-500/25 transition-all">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500/30 to-fuchsia-500/20 border border-purple-500/20 flex items-center justify-center mx-auto mb-3">
                <item.icon size={18} className="text-purple-300" />
              </div>
              <p className="font-outfit font-semibold text-white text-sm mb-1">{item.label}</p>
              <p className="text-white/40 text-xs">{item.sub}</p>
            </div>
          </FadeIn>
        ))}
      </div>
    </div>
  </section>
);

const RoadmapSection = () => (
  <section data-testid="roadmap-section" className="relative py-24 overflow-hidden">
    <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[600px] h-60 bg-purple-600/10 rounded-full blur-[100px] pointer-events-none" />
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <FadeIn><SectionLabel>Product Direction</SectionLabel></FadeIn>
      <FadeIn delay={100}>
        <h2 className="font-outfit font-bold text-3xl sm:text-4xl text-white mb-4 tracking-tight max-w-2xl">Where we're headed.</h2>
        <p className="text-white/50 text-base mb-12 max-w-2xl">Core platform, safety layer, parent controls, community, clubs, challenges, Times and gifting mechanics are built. ROMIND KIDS is now publicly available on Google Play. Our current phase focuses on Android launch, real-world user feedback and continued improvements.</p>
      </FadeIn>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {roadmapPhases.map((phase, i) => (
          <FadeIn key={phase.phase} delay={i * 80}>
            <div data-testid={`roadmap-phase-${i}`} className="glass-card p-5 h-full">
              <div className="text-[11px] font-outfit font-bold text-purple-400/60 tracking-widest mb-2">PHASE {phase.phase}</div>
              <h3 className="font-outfit font-bold text-white text-base mb-3">{phase.title}</h3>
              <div className="space-y-1.5 mb-4">
                {phase.items.map(it => <div key={it} className="text-xs text-white/40 flex items-center gap-1.5"><div className="w-1 h-1 rounded-full bg-purple-400/50 flex-shrink-0" />{it}</div>)}
              </div>
              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${phase.status === 'Completed' ? 'bg-emerald-500/15 text-emerald-300 border-emerald-400/20' : phase.status === 'Current Phase' ? 'bg-purple-500/20 text-purple-300 border-purple-400/25' : phase.status === 'Next' ? 'bg-cyan-500/15 text-cyan-300 border-cyan-400/20' : 'bg-white/5 text-white/35 border-white/10'}`}>
                {phase.status}
              </span>
            </div>
          </FadeIn>
        ))}
      </div>
    </div>
  </section>
);

const GooglePlaySection = () => {
  const { t } = useLocale();
  return (
    <section data-testid="google-play-section" className="relative overflow-hidden border-y border-purple-400/20 bg-[#080714] py-24">
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-purple-900/25 via-transparent to-cyan-900/25" />
      <div className="relative mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
        <FadeIn>
          <div className="mb-6 inline-flex h-16 w-16 items-center justify-center rounded-2xl border border-purple-400/35 bg-purple-500/15">
            <Smartphone size={28} className="text-purple-300" />
          </div>
          <SectionLabel>{t('download.available')}</SectionLabel>
          <h2 className="mb-4 font-outfit text-3xl font-bold tracking-tight text-white sm:text-4xl">{t('download.headline')}</h2>
          <p className="mx-auto mb-8 max-w-2xl text-base leading-relaxed text-white/65">{t('download.description')}</p>
          <div className="flex flex-col items-center justify-center gap-8 sm:flex-row">
            <DownloadButton />
            <DownloadQR />
          </div>
        </FadeIn>
      </div>
    </section>
  );
};

const FounderTeaserSection = () => (
  <section data-testid="founder-teaser-section" className="relative py-24 overflow-hidden">
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-60 bg-purple-600/10 rounded-full blur-[100px] pointer-events-none" />
    <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <FadeIn>
        <SectionLabel>Our Story</SectionLabel>
        <h2 className="font-outfit font-bold text-3xl sm:text-4xl text-white mb-4 tracking-tight">
          Built by parents, for children.
        </h2>
        <p className="text-white/55 text-base leading-relaxed mb-8 max-w-2xl mx-auto">
          ROMIND KIDS was created from personal experience — parents of three who saw what the 9–14 generation actually needs from a digital platform: a creative space designed around them, with a safety architecture designed around the adults who trust it. ROMIND KIDS is the first chapter of a wider planned ecosystem.
        </p>
        <Link to="/about" data-testid="founder-learn-more" className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm bg-purple-600/80 border border-purple-400/40 text-white hover:bg-purple-500 transition-all duration-200">
          Discover Our Story <ArrowRight size={14} />
        </Link>
      </FadeIn>
    </div>
  </section>
);

const Home = () => (
  <main>
    <HeroSection />
    <GiftCampaignSection />
    <AcademyTeaserSection />
    <VentureStudioTeaserSection />
    <ProblemSection />
    <SolutionSection />
    <FeaturesSection />
    <AppShowcaseSection />
    <DesignShowcaseSection />
    <SafetySection />
    <HowItWorksSection />
    <WhySection />
    <ProductPreviewSection />
    <RoadmapSection />
    <GooglePlaySection />
    <NewsletterSection />
    <FounderTeaserSection />
  </main>
);

export default Home;
